import {
  ConnectionModeEnum,
  createQueryParamsFromFilterClauses,
  EMPTY_OBJECT,
  FilterClauseType,
  getTypedEntries,
  NotificationVariants,
  PATCH,
  request,
  type FilterClause,
  type ToastProps,
} from '@talos/kyoko';
import { noop } from 'lodash-es';
import { useCallback } from 'react';
import { AdminPaths } from '../../../components/NavigationSidebar/hooks/getNavPath';
import type { MarketSecurityMode, MarketSecurityStatusLocal } from './types';
import { useSecurityMasterFilter, type SecurityMasterFilter } from './useSecurityMasterFilter';

export const isSecurityStatusEnabled = (mss: MarketSecurityStatusLocal) => {
  return mss.RequestedEnabled === ConnectionModeEnum.Up || mss.Enabled === ConnectionModeEnum.Up;
};

export const isMarketDataEnabled = (securityStatus: MarketSecurityStatusLocal) => {
  return securityStatus.Capabilities?.MarketData === true;
};

/** We can enable a security status if
 * 1. The market is not set to down and,
 * 2. The security status is not already set to up and,
 * 3. The security status is not already requested to be set to up
 */
export function canEnableSecurityStatus(status: MarketSecurityStatusLocal): boolean {
  return (
    status.MarketEnabled !== ConnectionModeEnum.Down &&
    status.Enabled !== ConnectionModeEnum.Up &&
    status.RequestedEnabled !== ConnectionModeEnum.Up
  );
}

export const updateSecurityStatus = ({
  orgApiEndpoint,
  mktSecStatus,
  data,
  addToast,
}: {
  orgApiEndpoint?: string;
  mktSecStatus: MarketSecurityStatusLocal;
  data: Partial<MarketSecurityMode>;
  addToast: (
    toast: Omit<ToastProps, 'id'> & {
      id?: string | undefined;
    }
  ) => void;
}) => {
  const endpoint = `${orgApiEndpoint}/market-security-modes/${mktSecStatus?.MarketAccount}/symbol/${mktSecStatus?.Symbol}`;
  return request<Partial<MarketSecurityMode>>(PATCH, endpoint, data)
    .then(res => {
      addToast({
        text: `Updated ${mktSecStatus?.Symbol} @ ${mktSecStatus?.MarketAccount}`,
        variant: NotificationVariants.Positive,
      });
    })
    .catch((e: ErrorEvent) => {
      addToast({
        text: e?.toString() || `Could not update ${mktSecStatus?.Symbol} @ ${mktSecStatus?.MarketAccount} `,
        variant: NotificationVariants.Negative,
      });
    });
};

/**
 * Hook that returns a stateful function to get the deeplink path to Security Master for navigating there
 * It takes in your Markets, MarketAccounts, Symbols filter, checks your selections against what is applicable in the
 * Security Master blotter, and then returns the valid path that the Security Master will accept.
 */
export const useGetSecurityMasterDeeplinkPath = () => {
  // We grab the filterable properties and validate the params below against these so we don't apply any incorrect filter
  const { filterableProperties } = useSecurityMasterFilter({ initialFilter: EMPTY_OBJECT, saveFilter: noop });

  const getSecurityMasterDeeplinkPath = useCallback(
    (params: Pick<SecurityMasterFilter, 'MarketAccounts' | 'Markets' | 'Symbols'>) => {
      const path = AdminPaths.SecurityMaster();
      const clauses = new Map<string, FilterClause>();

      getTypedEntries(params).forEach(([key, values]) => {
        if (values && values.length > 0) {
          const property = filterableProperties.find(p => p.key === key);
          if (!property || property?.control === 'text') {
            return;
          }

          const optionKeys = new Set(property.options);
          const acceptedValues = values.filter(value => optionKeys.has(value));

          clauses.set(key, {
            key,
            selections: acceptedValues,
            type: FilterClauseType.INCLUSIVE,
          });
        }
      });

      const queryParams = createQueryParamsFromFilterClauses(clauses);
      const queryString = new URLSearchParams(queryParams).toString();
      return queryString ? `${path}?${queryString}` : path;
    },
    [filterableProperties]
  );

  return getSecurityMasterDeeplinkPath;
};
